import React, { useMemo, useState } from 'react';
import { Bar, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Tooltip,
  Legend,
} from 'chart.js';
import {
  CIRCLE_OPTIONS,
  TOWER_PM_ZONE_KPIS,
  TOWER_PM_CMP_HEATMAP,
  TOWER_PM_STAGE_CHART_DATA,
  TOWER_PM_WORKSTREAM,
  TOWER_PM_SEGMENTATION,
  TOWER_PM_CONSECUTIVE_WO,
  TOWER_PM_DESCOPE,
  TOWER_PM_RFE1,
  TOWER_PM_DRILLDOWN_NONTECO,
  TOWER_PM_DRILLDOWN_ZERO_ZABBIX,
  TOWER_PM_DRILLDOWN_OUTAGE4X,
  TOWER_PM_INSIGHT_CHIPS,
  TOWER_PM_INSIGHTS,
  TOWER_PM_ACTIONS,
  TOWER_PM_RECOVERABLE_BANNER,
} from '../../data/towerPmData';
import {
  KpiCard,
  SectionTitle,
  ChipStrip,
  FilterSelect,
  SearchField,
  InsightCard,
  PriorityBadge,
  SortButton,
  EmptyState,
  PeriodUnavailableState,
  SubTabPanel,
  ProgressBar,
  LoadMoreButton,
  palette,
  chartBaseOptions,
  InsightBanner,
  toneAccent,
} from '../dashboardPrimitives';

ChartJS.register(CategoryScale, LinearScale, BarElement, ArcElement, Tooltip, Legend);

/* ── Circle colour map ── */
const CIRCLE_COLORS = {
  HP: '#3b82f6',
  KS: '#8b5cf6',
  JA: '#ef4444',
  UW: '#f59e0b',
};

/* ── Drilldown circle options ── */
const DRILLDOWN_CIRCLE_OPTIONS = [
  { value: 'ALL', label: 'All Circles' },
  { value: 'HP', label: 'HP' },
  { value: 'JA', label: 'JA' },
  { value: 'KS', label: 'KS' },
  { value: 'UW', label: 'UW' },
];

/* ── Sort helper ── */
function sortValue(row, key) {
  switch (key) {
    case 'cmp':
    case 'circle':
    case 'site_id':
    case 'fac_id':
    case 'site_type':
    case 'state':
      return row[key] ?? '';
    default:
      return row[key] ?? 0;
  }
}

function applySortToRows(rows, sortState) {
  if (!sortState.key) return rows;
  return [...rows].sort((left, right) => {
    const leftValue = sortValue(left, sortState.key);
    const rightValue = sortValue(right, sortState.key);
    if (typeof leftValue === 'string') {
      return sortState.dir === 'asc'
        ? leftValue.localeCompare(rightValue)
        : rightValue.localeCompare(leftValue);
    }
    return sortState.dir === 'asc' ? leftValue - rightValue : rightValue - leftValue;
  });
}

const PAGE_SIZE = 20;

export default function TowerPM({ selectedMonth }) {
  /* ── Global filter state ── */
  const [activeCircle, setActiveCircle] = useState('ALL');

  /* ── Heatmap sort state ── */
  const [heatmapSort, setHeatmapSort] = useState({ key: 'scope', dir: 'desc' });

  /* ── Stage-1 drilldown state ── */
  const [drilldownTab, setDrilldownTab] = useState(0);
  const [ddSearch0, setDdSearch0] = useState('');
  const [ddSearch1, setDdSearch1] = useState('');
  const [ddSearch2, setDdSearch2] = useState('');
  const [ddCmpFilter0, setDdCmpFilter0] = useState('');
  const [ddCmpFilter1, setDdCmpFilter1] = useState('');
  const [ddCmpFilter2, setDdCmpFilter2] = useState('');
  const [ddCircleFilter0, setDdCircleFilter0] = useState('ALL');
  const [ddCircleFilter1, setDdCircleFilter1] = useState('ALL');
  const [ddCircleFilter2, setDdCircleFilter2] = useState('ALL');
  const [ddPage0, setDdPage0] = useState(PAGE_SIZE);
  const [ddPage1, setDdPage1] = useState(PAGE_SIZE);
  const [ddPage2, setDdPage2] = useState(PAGE_SIZE);

  /* ── Workstream sort ── */
  const [wsSort, setWsSort] = useState({ key: 'total', dir: 'desc' });

  /* ── Consecutive WO state ── */
  const [consecSearch, setConsecSearch] = useState('');
  const [consecCmpFilter, setConsecCmpFilter] = useState('');
  const [consecPage, setConsecPage] = useState(PAGE_SIZE);

  /* ── Descope / RFE1 pages ── */
  const [descopePage, setDescopePage] = useState(PAGE_SIZE);
  const [rfe1Page, setRfe1Page] = useState(PAGE_SIZE);

  /* ── Filtered CMP heatmap rows ── */
  const filteredHeatmap = useMemo(() => {
    const rows = activeCircle === 'ALL'
      ? TOWER_PM_CMP_HEATMAP
      : TOWER_PM_CMP_HEATMAP.filter((row) => row.circle === activeCircle);
    return applySortToRows(rows, heatmapSort);
  }, [activeCircle, heatmapSort]);

  /* ── Zone KPIs computed from filtered heatmap ── */
  const zoneKpis = useMemo(() => {
    const h = filteredHeatmap;
    const totalScope = h.reduce((s, r) => s + r.scope, 0);
    const totalPassS3 = h.reduce((s, r) => s + r.passS3, 0);
    const totalFailS1 = h.reduce((s, r) => s + r.failS1, 0);
    const totalFailS23 = h.reduce((s, r) => s + r.failS23, 0);
    const totalOutage4x = h.reduce((s, r) => s + r.outages4x, 0);
    const totalTripleRisk = h.reduce((s, r) => s + r.tripleRisk, 0);

    const s1PassRate = totalScope > 0
      ? (((totalScope - totalFailS1) / totalScope) * 100).toFixed(1)
      : '0.0';
    const s3PassRate = totalScope > 0
      ? ((totalPassS3 / totalScope) * 100).toFixed(1)
      : '0.0';
    const s23FailPct = totalScope > 0
      ? ((totalFailS23 / totalScope) * 100).toFixed(1)
      : '0.0';
    const s1FailPct = totalScope > 0
      ? ((totalFailS1 / totalScope) * 100).toFixed(1)
      : '0.0';

    return [
      {
        label: 'Stage-1 Pass Rate',
        value: `${s1PassRate}%`,
        sub: `${(totalScope - totalFailS1).toLocaleString('en-IN')} / ${totalScope.toLocaleString('en-IN')} sites pass TECO + Zabbix check`,
        dot: palette.success,
      },
      {
        label: 'Stage-3 Pass (100% Payout)',
        value: `${totalPassS3.toLocaleString('en-IN')}`,
        sub: `${s3PassRate}% of scope · Full payout eligible`,
        dot: palette.success,
      },
      {
        label: 'Stage-2/3 Fail (50% Payout)',
        value: `${totalFailS23.toLocaleString('en-IN')}`,
        sub: `${s23FailPct}% of scope · KPI quality check fail`,
        dot: palette.orange,
      },
      {
        label: 'Stage-1 Fail (ZERO Payout)',
        value: `${totalFailS1.toLocaleString('en-IN')}`,
        sub: `${s1FailPct}% of scope · Non-TECO or Zero Zabbix`,
        dot: palette.danger,
      },
      {
        label: 'Sites: Outages >4× (S2 fail)',
        value: `${totalOutage4x}`,
        sub: TOWER_PM_ZONE_KPIS[4].sub,
        dot: palette.danger,
      },
      {
        label: '3× Penalty Risk Sites',
        value: `${totalTripleRisk}`,
        sub: TOWER_PM_ZONE_KPIS[5].sub,
        dot: palette.warning,
      },
    ];
  }, [filteredHeatmap]);

  /* ── Heatmap totals row ── */
  const heatmapTotals = useMemo(() => {
    const h = filteredHeatmap;
    return {
      scope: h.reduce((s, r) => s + r.scope, 0),
      nonTeco: h.reduce((s, r) => s + r.nonTeco, 0),
      zeroZabbix: h.reduce((s, r) => s + r.zeroZabbix, 0),
      zeroPayout: h.reduce((s, r) => s + r.zeroPayout, 0),
      outages4x: h.reduce((s, r) => s + r.outages4x, 0),
      payout50: h.reduce((s, r) => s + r.payout50, 0),
      payout100: h.reduce((s, r) => s + r.payout100, 0),
      tripleRisk: h.reduce((s, r) => s + r.tripleRisk, 0),
      pmTeco: h.reduce((s, r) => s + r.pmTeco, 0),
      assetVis: h.reduce((s, r) => s + r.assetVis, 0),
      validKpi: h.reduce((s, r) => s + r.validKpi, 0),
      outLte4: h.reduce((s, r) => s + r.outLte4, 0),
      failS1: h.reduce((s, r) => s + r.failS1, 0),
      failS23: h.reduce((s, r) => s + r.failS23, 0),
      passS3: h.reduce((s, r) => s + r.passS3, 0),
      consec3: h.reduce((s, r) => s + r.consec3, 0),
    };
  }, [filteredHeatmap]);

  /* ── Stage breakdown chart data ── */
  const stageDistChartData = useMemo(() => {
    const labels = filteredHeatmap.map((r) => r.cmp);
    return {
      labels,
      datasets: [
        {
          label: '100% Payout (S3)',
          data: filteredHeatmap.map((r) => r.passS3),
          backgroundColor: '#22c55e',
          borderRadius: 2,
        },
        {
          label: '50% Payout (S2/3)',
          data: filteredHeatmap.map((r) => r.failS23),
          backgroundColor: '#f97316',
          borderRadius: 2,
        },
        {
          label: 'ZERO Payout (S1)',
          data: filteredHeatmap.map((r) => r.failS1),
          backgroundColor: '#ef4444',
          borderRadius: 2,
        },
      ],
    };
  }, [filteredHeatmap]);

  const s1DriverChartData = useMemo(() => {
    const labels = filteredHeatmap.map((r) => r.cmp);
    return {
      labels,
      datasets: [
        {
          label: 'Non-TECO PM',
          data: filteredHeatmap.map((r) => r.nonTeco),
          backgroundColor: '#ef4444',
          borderRadius: 2,
        },
        {
          label: 'Zero Zabbix',
          data: filteredHeatmap.map((r) => r.zeroZabbix),
          backgroundColor: '#f97316',
          borderRadius: 2,
        },
      ],
    };
  }, [filteredHeatmap]);

  const zonePieData = useMemo(() => {
    const t = heatmapTotals;
    return {
      labels: ['100% Payout', '50% Payout', 'ZERO Payout'],
      datasets: [
        {
          data: [t.passS3, t.failS23, t.failS1],
          backgroundColor: ['#22c55e', '#f97316', '#ef4444'],
          borderWidth: 0,
        },
      ],
    };
  }, [heatmapTotals]);

  const outageChartData = useMemo(() => {
    const labels = filteredHeatmap.map((r) => r.cmp);
    const { thresholds, colors } = TOWER_PM_STAGE_CHART_DATA.outageChart;
    return {
      labels,
      datasets: [
        {
          label: 'Outages >4 Times',
          data: filteredHeatmap.map((r) => r.outages4x),
          backgroundColor: filteredHeatmap.map((r) => {
            if (r.outages4x >= thresholds.red) return colors.red;
            if (r.outages4x >= thresholds.orange) return colors.orange;
            return colors.yellow;
          }),
          borderRadius: 4,
        },
      ],
    };
  }, [filteredHeatmap]);

  /* ── Stage-1 drilldown data ── */
  const drilldownNonTeco = useMemo(() => {
    let rows = activeCircle === 'ALL'
      ? TOWER_PM_DRILLDOWN_NONTECO
      : TOWER_PM_DRILLDOWN_NONTECO.filter((r) => r.r === activeCircle);
    if (ddCircleFilter0 !== 'ALL') rows = rows.filter((r) => r.r === ddCircleFilter0);
    if (ddCmpFilter0) rows = rows.filter((r) => r.c === ddCmpFilter0);
    if (ddSearch0.trim()) {
      const q = ddSearch0.toLowerCase();
      rows = rows.filter((r) => r.s.toLowerCase().includes(q) || r.f.toLowerCase().includes(q) || r.c.toLowerCase().includes(q));
    }
    return rows;
  }, [activeCircle, ddCircleFilter0, ddCmpFilter0, ddSearch0]);

  const drilldownZeroZabbix = useMemo(() => {
    let rows = activeCircle === 'ALL'
      ? TOWER_PM_DRILLDOWN_ZERO_ZABBIX
      : TOWER_PM_DRILLDOWN_ZERO_ZABBIX.filter((r) => r.r === activeCircle);
    if (ddCircleFilter1 !== 'ALL') rows = rows.filter((r) => r.r === ddCircleFilter1);
    if (ddCmpFilter1) rows = rows.filter((r) => r.c === ddCmpFilter1);
    if (ddSearch1.trim()) {
      const q = ddSearch1.toLowerCase();
      rows = rows.filter((r) => r.s.toLowerCase().includes(q) || r.f.toLowerCase().includes(q) || r.c.toLowerCase().includes(q));
    }
    return rows;
  }, [activeCircle, ddCircleFilter1, ddCmpFilter1, ddSearch1]);

  const drilldownOutage4x = useMemo(() => {
    let rows = activeCircle === 'ALL'
      ? TOWER_PM_DRILLDOWN_OUTAGE4X
      : TOWER_PM_DRILLDOWN_OUTAGE4X.filter((r) => r.r === activeCircle);
    if (ddCircleFilter2 !== 'ALL') rows = rows.filter((r) => r.r === ddCircleFilter2);
    if (ddCmpFilter2) rows = rows.filter((r) => r.c === ddCmpFilter2);
    if (ddSearch2.trim()) {
      const q = ddSearch2.toLowerCase();
      rows = rows.filter((r) => r.s.toLowerCase().includes(q) || r.f.toLowerCase().includes(q) || r.c.toLowerCase().includes(q));
    }
    return [...rows].sort((a, b) => (b.d ?? 0) - (a.d ?? 0));
  }, [activeCircle, ddCircleFilter2, ddCmpFilter2, ddSearch2]);

  /* ── Workstream filtered + sorted ── */
  const filteredWorkstream = useMemo(() => {
    const rows = activeCircle === 'ALL'
      ? TOWER_PM_WORKSTREAM
      : TOWER_PM_WORKSTREAM.filter((r) => r.circle === activeCircle);
    return applySortToRows(rows, wsSort);
  }, [activeCircle, wsSort]);

  const wsTotals = useMemo(() => {
    const h = filteredWorkstream;
    return {
      total: h.reduce((s, r) => s + r.total, 0),
      spmTeco: h.reduce((s, r) => s + r.spmTeco, 0),
      spmWocl: h.reduce((s, r) => s + r.spmWocl, 0),
      spmNd: h.reduce((s, r) => s + r.spmNd, 0),
      spmNa: h.reduce((s, r) => s + r.spmNa, 0),
      gbmTeco: h.reduce((s, r) => s + r.gbmTeco, 0),
      solTeco: h.reduce((s, r) => s + r.solTeco, 0),
      finalTeco: h.reduce((s, r) => s + r.finalTeco, 0),
      finalFail: h.reduce((s, r) => s + r.finalFail, 0),
    };
  }, [filteredWorkstream]);

  /* ── Consecutive WO filtered ── */
  const filteredConsec = useMemo(() => {
    let rows = TOWER_PM_CONSECUTIVE_WO.sites;
    if (activeCircle !== 'ALL') rows = rows.filter((r) => r.state === activeCircle);
    if (consecCmpFilter) rows = rows.filter((r) => r.cmp === consecCmpFilter);
    if (consecSearch.trim()) {
      const q = consecSearch.toLowerCase();
      rows = rows.filter((r) =>
        r.site_id.toLowerCase().includes(q)
        || r.fac_id.toLowerCase().includes(q)
        || r.cmp.toLowerCase().includes(q),
      );
    }
    return rows;
  }, [activeCircle, consecCmpFilter, consecSearch]);

  const consecCmpOptions = useMemo(() => {
    const cmps = [...new Set(TOWER_PM_CONSECUTIVE_WO.sites.map((r) => r.cmp))].sort();
    return [{ value: '', label: 'All CMPs' }, ...cmps.map((c) => ({ value: c, label: c }))];
  }, []);

  /* ── Sort toggle helpers ── */
  const updateHeatmapSort = (key) => {
    setHeatmapSort((cur) => ({
      key,
      dir: cur.key === key && cur.dir === 'desc' ? 'asc' : 'desc',
    }));
  };

  const updateWsSort = (key) => {
    setWsSort((cur) => ({
      key,
      dir: cur.key === key && cur.dir === 'desc' ? 'asc' : 'desc',
    }));
  };

  /* ── CMP filter options for drilldown ── */
  const ddCmpOptions = useMemo(() => {
    const cmps = [...new Set(TOWER_PM_CMP_HEATMAP.map((r) => r.cmp))].sort();
    return [{ value: '', label: 'All CMPs' }, ...cmps.map((c) => ({ value: c, label: c }))];
  }, []);

  /* ── Period guard ── */
  if (selectedMonth !== "Feb'26") {
    return <PeriodUnavailableState monthLabel={selectedMonth} />;
  }

  /* ═══════════ RENDER ═══════════ */
  return (
    <div className="space-y-6">

      {/* ── 1. Filter Strip ── */}
      <ChipStrip groups={[{ label: 'Circle', value: activeCircle, onChange: setActiveCircle, options: CIRCLE_OPTIONS }]} />

      {/* ── 3. Zone PM Summary KPIs ── */}
      <SectionTitle dot={palette.accent}>Zone PM Summary - Feb'26</SectionTitle>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-6">
        {zoneKpis.map((kpi) => (
          <KpiCard
            key={kpi.label}
            label={kpi.label}
            value={kpi.value}
            sub={kpi.sub}
            dot={kpi.dot}
            valueColor={kpi.dot}
          />
        ))}
      </div>

      {/* ── 4. PM Stage Computation Heatmap Table ── */}
      <SectionTitle dot={palette.navy}>PM Stage Computation Heatmap - CMP Level</SectionTitle>
      {filteredHeatmap.length ? (
        <div className="table-scroll-shell">
          <table className="evidence-table">
            <thead>
              <tr>
                <th><SortButton label="CMP" sortKey="cmp" sortState={heatmapSort} onSort={updateHeatmapSort} /></th>
                <th>Circle</th>
                <th><SortButton label="Scope" sortKey="scope" sortState={heatmapSort} onSort={updateHeatmapSort} /></th>
                <th><SortButton label="PM TECO" sortKey="pmTeco" sortState={heatmapSort} onSort={updateHeatmapSort} /></th>
                <th><SortButton label="Non-TECO" sortKey="nonTeco" sortState={heatmapSort} onSort={updateHeatmapSort} /></th>
                <th><SortButton label="Zero Zabbix" sortKey="zeroZabbix" sortState={heatmapSort} onSort={updateHeatmapSort} /></th>
                <th><SortButton label="ZERO Payout" sortKey="zeroPayout" sortState={heatmapSort} onSort={updateHeatmapSort} /></th>
                <th><SortButton label="Outages >4×" sortKey="outages4x" sortState={heatmapSort} onSort={updateHeatmapSort} /></th>
                <th><SortButton label="50% Payout" sortKey="payout50" sortState={heatmapSort} onSort={updateHeatmapSort} /></th>
                <th><SortButton label="100% Payout" sortKey="payout100" sortState={heatmapSort} onSort={updateHeatmapSort} /></th>
                <th><SortButton label="3× Risk" sortKey="tripleRisk" sortState={heatmapSort} onSort={updateHeatmapSort} /></th>
              </tr>
            </thead>
            <tbody>
              {filteredHeatmap.map((row) => (
                <tr key={row.cmp}>
                  <td className="font-semibold text-slate-700">{row.cmp}</td>
                  <td>
                    <span
                      className="inline-flex rounded-full px-2 py-1 text-[10px] font-semibold text-white"
                      style={{ backgroundColor: CIRCLE_COLORS[row.circle] ?? palette.slate }}
                    >
                      {row.circle}
                    </span>
                  </td>
                  <td className="mono-cell">{row.scope.toLocaleString('en-IN')}</td>
                  <td className="mono-cell">{row.pmTeco.toLocaleString('en-IN')}</td>
                  <td className="mono-cell" style={{ color: row.nonTeco > 100 ? palette.danger : undefined }}>{row.nonTeco}</td>
                  <td className="mono-cell" style={{ color: row.zeroZabbix > 50 ? palette.danger : undefined }}>{row.zeroZabbix}</td>
                  <td className="mono-cell font-semibold" style={{ color: palette.danger }}>{row.zeroPayout}</td>
                  <td className="mono-cell" style={{ color: row.outages4x >= 30 ? palette.danger : row.outages4x >= 15 ? palette.warning : undefined }}>{row.outages4x}</td>
                  <td className="mono-cell" style={{ color: palette.warning }}>{row.payout50.toLocaleString('en-IN')}</td>
                  <td className="mono-cell" style={{ color: palette.success }}>{row.payout100.toLocaleString('en-IN')}</td>
                  <td className="mono-cell" style={{ color: row.tripleRisk > 10 ? palette.danger : row.tripleRisk > 0 ? palette.warning : undefined }}>{row.tripleRisk}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="font-semibold">
                <td>TOTAL</td>
                <td />
                <td className="mono-cell">{heatmapTotals.scope.toLocaleString('en-IN')}</td>
                <td className="mono-cell">{heatmapTotals.pmTeco.toLocaleString('en-IN')}</td>
                <td className="mono-cell">{heatmapTotals.nonTeco}</td>
                <td className="mono-cell">{heatmapTotals.zeroZabbix}</td>
                <td className="mono-cell" style={{ color: palette.danger }}>{heatmapTotals.zeroPayout}</td>
                <td className="mono-cell">{heatmapTotals.outages4x}</td>
                <td className="mono-cell">{heatmapTotals.payout50.toLocaleString('en-IN')}</td>
                <td className="mono-cell">{heatmapTotals.passS3.toLocaleString('en-IN')}</td>
                <td className="mono-cell">{heatmapTotals.tripleRisk}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      ) : (
        <EmptyState />
      )}

      {/* ── 5. Stage Breakdown Charts (2×2 grid) ── */}
      <SectionTitle dot={palette.accent}>Stage Breakdown Charts</SectionTitle>
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {/* 5a - Stage Distribution Stacked Bar */}
        <div className="card-surface p-6">
          <h3 className="mb-4 text-[13px] font-bold uppercase tracking-wide text-slate-500">Payout Stage Distribution by CMP</h3>
          <div style={{ height: 320 }}>
            <Bar
              data={stageDistChartData}
              options={{
                ...chartBaseOptions,
                plugins: {
                  ...chartBaseOptions.plugins,
                  legend: { display: true, position: 'top', labels: { ...chartBaseOptions.plugins.legend.labels } },
                  tooltip: {
                    ...chartBaseOptions.plugins.tooltip,
                    callbacks: { label: (ctx) => ` ${ctx.dataset.label}: ${ctx.raw.toLocaleString('en-IN')}` },
                  },
                },
                scales: {
                  x: { stacked: true, grid: { display: false }, border: { display: false }, ticks: { color: palette.slateDeep, font: { family: 'Inter', size: 9, weight: '500' }, maxRotation: 45 } },
                  y: { stacked: true, grid: { color: palette.border }, border: { display: false }, ticks: { color: palette.slateDeep, font: { family: 'JetBrains Mono', size: 10, weight: '500' } } },
                },
              }}
            />
          </div>
        </div>

        {/* 5b - S1 Failure Driver */}
        <div className="card-surface p-6">
          <h3 className="mb-4 text-[13px] font-bold uppercase tracking-wide text-slate-500">Stage-1 Failure Drivers: Non-TECO vs Zero Zabbix</h3>
          <div style={{ height: 320 }}>
            <Bar
              data={s1DriverChartData}
              options={{
                ...chartBaseOptions,
                plugins: {
                  ...chartBaseOptions.plugins,
                  legend: { display: true, position: 'top', labels: { ...chartBaseOptions.plugins.legend.labels } },
                  tooltip: {
                    ...chartBaseOptions.plugins.tooltip,
                    callbacks: { label: (ctx) => ` ${ctx.dataset.label}: ${ctx.raw}` },
                  },
                },
                scales: {
                  x: { stacked: true, grid: { display: false }, border: { display: false }, ticks: { color: palette.slateDeep, font: { family: 'Inter', size: 9, weight: '500' }, maxRotation: 45 } },
                  y: { stacked: true, grid: { color: palette.border }, border: { display: false }, ticks: { color: palette.slateDeep, font: { family: 'JetBrains Mono', size: 10, weight: '500' } } },
                },
              }}
            />
          </div>
        </div>

        {/* 5c - Zone Pie */}
        <div className="card-surface p-6">
          <h3 className="mb-4 text-[13px] font-bold uppercase tracking-wide text-slate-500">Zone Payout Distribution</h3>
          <div className="mx-auto" style={{ height: 280, maxWidth: 320 }}>
            <Doughnut
              data={zonePieData}
              options={{
                ...chartBaseOptions,
                cutout: '55%',
                plugins: {
                  ...chartBaseOptions.plugins,
                  legend: { display: true, position: 'bottom', labels: { ...chartBaseOptions.plugins.legend.labels } },
                  tooltip: {
                    ...chartBaseOptions.plugins.tooltip,
                    callbacks: { label: (ctx) => ` ${ctx.label}: ${ctx.raw.toLocaleString('en-IN')} sites` },
                  },
                },
              }}
            />
          </div>
        </div>

        {/* 5d - Outages >4× */}
        <div className="card-surface p-6">
          <h3 className="mb-4 text-[13px] font-bold uppercase tracking-wide text-slate-500">Outages {'>'}4 Times by CMP</h3>
          <div style={{ height: 280 }}>
            <Bar
              data={outageChartData}
              options={{
                ...chartBaseOptions,
                plugins: {
                  ...chartBaseOptions.plugins,
                  legend: { display: false },
                  tooltip: {
                    ...chartBaseOptions.plugins.tooltip,
                    callbacks: { label: (ctx) => ` ${ctx.raw} sites` },
                  },
                },
                scales: {
                  x: { grid: { display: false }, border: { display: false }, ticks: { color: palette.slateDeep, font: { family: 'Inter', size: 9, weight: '500' }, maxRotation: 45 } },
                  y: { grid: { color: palette.border }, border: { display: false }, ticks: { color: palette.slateDeep, font: { family: 'JetBrains Mono', size: 10, weight: '500' } } },
                },
              }}
            />
          </div>
        </div>
      </div>

      {/* ── 6. Stage-1 Failure Site Drilldown ── */}
      <SectionTitle dot={palette.danger}>Stage-1 Failure Site Drilldown</SectionTitle>
      <SubTabPanel
        tabs={[
          { label: 'Non-TECO', count: drilldownNonTeco.length },
          { label: 'Zero Zabbix', count: drilldownZeroZabbix.length },
          { label: 'Outage >4×', count: drilldownOutage4x.length },
        ]}
        activeTab={drilldownTab}
        onTabChange={setDrilldownTab}
      >
        {drilldownTab === 0 && (
          <div className="space-y-4">
            <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-end">
              <SearchField value={ddSearch0} onChange={(v) => { setDdSearch0(v); setDdPage0(PAGE_SIZE); }} placeholder="Search sites..." className="flex-1 sm:min-w-[220px]" />
              <FilterSelect label="CMP" value={ddCmpFilter0} onChange={(v) => { setDdCmpFilter0(v); setDdPage0(PAGE_SIZE); }} options={ddCmpOptions} />
              <FilterSelect label="Circle" value={ddCircleFilter0} onChange={(v) => { setDdCircleFilter0(v); setDdPage0(PAGE_SIZE); }} options={DRILLDOWN_CIRCLE_OPTIONS} />
            </div>
            {drilldownNonTeco.length ? (
              <>
                <div className="table-scroll-shell">
                  <table className="evidence-table">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Site ID</th>
                        <th>Facility ID</th>
                        <th>CMP</th>
                        <th>Circle</th>
                        <th>Site Type</th>
                      </tr>
                    </thead>
                    <tbody>
                      {drilldownNonTeco.slice(0, ddPage0).map((row, i) => (
                        <tr key={`nt-${row.s}-${i}`}>
                          <td className="mono-cell text-slate-400">{i + 1}</td>
                          <td className="mono-cell font-medium text-slate-700">{row.s}</td>
                          <td className="mono-cell text-slate-500">{row.f}</td>
                          <td>{row.c}</td>
                          <td>
                            <span className="inline-flex rounded-full px-2 py-1 text-[10px] font-semibold text-white" style={{ backgroundColor: CIRCLE_COLORS[row.r] ?? palette.slate }}>
                              {row.r}
                            </span>
                          </td>
                          <td className="mono-cell">{row.t}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {ddPage0 < drilldownNonTeco.length && (
                  <LoadMoreButton remaining={drilldownNonTeco.length - ddPage0} onClick={() => setDdPage0((p) => p + PAGE_SIZE)} />
                )}
              </>
            ) : (
              <EmptyState icon="search" />
            )}
          </div>
        )}

        {drilldownTab === 1 && (
          <div className="space-y-4">
            <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-end">
              <SearchField value={ddSearch1} onChange={(v) => { setDdSearch1(v); setDdPage1(PAGE_SIZE); }} placeholder="Search sites..." className="flex-1 sm:min-w-[220px]" />
              <FilterSelect label="CMP" value={ddCmpFilter1} onChange={(v) => { setDdCmpFilter1(v); setDdPage1(PAGE_SIZE); }} options={ddCmpOptions} />
              <FilterSelect label="Circle" value={ddCircleFilter1} onChange={(v) => { setDdCircleFilter1(v); setDdPage1(PAGE_SIZE); }} options={DRILLDOWN_CIRCLE_OPTIONS} />
            </div>
            {drilldownZeroZabbix.length ? (
              <>
                <div className="table-scroll-shell">
                  <table className="evidence-table">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Site ID</th>
                        <th>Facility ID</th>
                        <th>CMP</th>
                        <th>Circle</th>
                        <th>Site Type</th>
                      </tr>
                    </thead>
                    <tbody>
                      {drilldownZeroZabbix.slice(0, ddPage1).map((row, i) => (
                        <tr key={`zz-${row.s}-${i}`}>
                          <td className="mono-cell text-slate-400">{i + 1}</td>
                          <td className="mono-cell font-medium text-slate-700">{row.s}</td>
                          <td className="mono-cell text-slate-500">{row.f}</td>
                          <td>{row.c}</td>
                          <td>
                            <span className="inline-flex rounded-full px-2 py-1 text-[10px] font-semibold text-white" style={{ backgroundColor: CIRCLE_COLORS[row.r] ?? palette.slate }}>
                              {row.r}
                            </span>
                          </td>
                          <td className="mono-cell">{row.t}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {ddPage1 < drilldownZeroZabbix.length && (
                  <LoadMoreButton remaining={drilldownZeroZabbix.length - ddPage1} onClick={() => setDdPage1((p) => p + PAGE_SIZE)} />
                )}
              </>
            ) : (
              <EmptyState icon="search" />
            )}
          </div>
        )}

        {drilldownTab === 2 && (
          <div className="space-y-4">
            <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-end">
              <SearchField value={ddSearch2} onChange={(v) => { setDdSearch2(v); setDdPage2(PAGE_SIZE); }} placeholder="Search sites..." className="flex-1 sm:min-w-[220px]" />
              <FilterSelect label="CMP" value={ddCmpFilter2} onChange={(v) => { setDdCmpFilter2(v); setDdPage2(PAGE_SIZE); }} options={ddCmpOptions} />
              <FilterSelect label="Circle" value={ddCircleFilter2} onChange={(v) => { setDdCircleFilter2(v); setDdPage2(PAGE_SIZE); }} options={DRILLDOWN_CIRCLE_OPTIONS} />
            </div>
            {drilldownOutage4x.length ? (
              <>
                <div className="table-scroll-shell">
                  <table className="evidence-table">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Site ID</th>
                        <th>Facility ID</th>
                        <th>CMP</th>
                        <th>Circle</th>
                        <th>Site Type</th>
                        <th>Outage Mins</th>
                      </tr>
                    </thead>
                    <tbody>
                      {drilldownOutage4x.slice(0, ddPage2).map((row, i) => (
                        <tr key={`o4-${row.s}-${i}`}>
                          <td className="mono-cell text-slate-400">{i + 1}</td>
                          <td className="mono-cell font-medium text-slate-700">{row.s}</td>
                          <td className="mono-cell text-slate-500">{row.f}</td>
                          <td>{row.c}</td>
                          <td>
                            <span className="inline-flex rounded-full px-2 py-1 text-[10px] font-semibold text-white" style={{ backgroundColor: CIRCLE_COLORS[row.r] ?? palette.slate }}>
                              {row.r}
                            </span>
                          </td>
                          <td className="mono-cell">{row.t}</td>
                          <td className="mono-cell font-semibold" style={{ color: (row.d ?? 0) >= 150 ? palette.danger : palette.warning }}>{row.d ?? '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {ddPage2 < drilldownOutage4x.length && (
                  <LoadMoreButton remaining={drilldownOutage4x.length - ddPage2} onClick={() => setDdPage2((p) => p + PAGE_SIZE)} />
                )}
              </>
            ) : (
              <EmptyState icon="search" />
            )}
          </div>
        )}
      </SubTabPanel>

      {/* ── 7. PM Work Stream Execution ── */}
      <SectionTitle dot={palette.navy}>PM Work Stream Execution</SectionTitle>
      <div className="card-surface overflow-hidden">
        <div className="table-scroll-shell">
          <table className="evidence-table">
            <thead>
              <tr>
                <th><SortButton label="CMP" sortKey="cmp" sortState={wsSort} onSort={updateWsSort} /></th>
                <th>Circle</th>
                <th><SortButton label="Total WOs" sortKey="total" sortState={wsSort} onSort={updateWsSort} /></th>
                <th><SortButton label="SPM TECO" sortKey="spmTeco" sortState={wsSort} onSort={updateWsSort} /></th>
                <th>TECO %</th>
                <th><SortButton label="WOCL" sortKey="spmWocl" sortState={wsSort} onSort={updateWsSort} /></th>
                <th><SortButton label="ND" sortKey="spmNd" sortState={wsSort} onSort={updateWsSort} /></th>
                <th><SortButton label="NA" sortKey="spmNa" sortState={wsSort} onSort={updateWsSort} /></th>
                <th><SortButton label="GBM TECO" sortKey="gbmTeco" sortState={wsSort} onSort={updateWsSort} /></th>
                <th><SortButton label="Solar TECO" sortKey="solTeco" sortState={wsSort} onSort={updateWsSort} /></th>
                <th><SortButton label="Final TECO" sortKey="finalTeco" sortState={wsSort} onSort={updateWsSort} /></th>
                <th><SortButton label="Final Fail" sortKey="finalFail" sortState={wsSort} onSort={updateWsSort} /></th>
              </tr>
            </thead>
            <tbody>
              {filteredWorkstream.map((row) => {
                const tecoPct = row.total > 0 ? ((row.spmTeco / row.total) * 100).toFixed(1) : '0.0';
                return (
                  <tr key={row.cmp}>
                    <td className="font-semibold text-slate-700">{row.cmp}</td>
                    <td>
                      <span className="inline-flex rounded-full px-2 py-1 text-[10px] font-semibold text-white" style={{ backgroundColor: CIRCLE_COLORS[row.circle] ?? palette.slate }}>
                        {row.circle}
                      </span>
                    </td>
                    <td className="mono-cell">{row.total.toLocaleString('en-IN')}</td>
                    <td className="mono-cell">{row.spmTeco.toLocaleString('en-IN')}</td>
                    <td>
                      <ProgressBar value={parseFloat(tecoPct)} color={parseFloat(tecoPct) >= 95 ? palette.success : parseFloat(tecoPct) >= 85 ? palette.warning : palette.danger} />
                    </td>
                    <td className="mono-cell" style={{ color: row.spmWocl > 20 ? palette.warning : undefined }}>{row.spmWocl}</td>
                    <td className="mono-cell">{row.spmNd}</td>
                    <td className="mono-cell" style={{ color: row.spmNa > 50 ? palette.danger : undefined }}>{row.spmNa}</td>
                    <td className="mono-cell">{row.gbmTeco}</td>
                    <td className="mono-cell">{row.solTeco}</td>
                    <td className="mono-cell">{row.finalTeco.toLocaleString('en-IN')}</td>
                    <td className="mono-cell font-semibold" style={{ color: row.finalFail > 50 ? palette.danger : row.finalFail > 0 ? palette.warning : palette.success }}>{row.finalFail}</td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="font-semibold">
                <td>TOTAL</td>
                <td />
                <td className="mono-cell">{wsTotals.total.toLocaleString('en-IN')}</td>
                <td className="mono-cell">{wsTotals.spmTeco.toLocaleString('en-IN')}</td>
                <td>
                  <ProgressBar value={wsTotals.total > 0 ? (wsTotals.spmTeco / wsTotals.total) * 100 : 0} color={palette.success} />
                </td>
                <td className="mono-cell">{wsTotals.spmWocl}</td>
                <td className="mono-cell">{wsTotals.spmNd}</td>
                <td className="mono-cell">{wsTotals.spmNa}</td>
                <td className="mono-cell">{wsTotals.gbmTeco}</td>
                <td className="mono-cell">{wsTotals.solTeco}</td>
                <td className="mono-cell">{wsTotals.finalTeco.toLocaleString('en-IN')}</td>
                <td className="mono-cell">{wsTotals.finalFail}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      {/* ── 8. Site & Equipment Segmentation ── */}
      <SectionTitle dot={palette.accent}>Site &amp; Equipment Segmentation</SectionTitle>
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        {/* Site Type */}
        <div className="card-surface p-6">
          <h3 className="mb-4 text-[13px] font-bold uppercase tracking-wide text-slate-500">Site Type Mix</h3>
          <div className="mx-auto" style={{ height: 260, maxWidth: 280 }}>
            <Doughnut
              data={{
                labels: TOWER_PM_SEGMENTATION.siteType.labels,
                datasets: [{
                  data: TOWER_PM_SEGMENTATION.siteType.data,
                  backgroundColor: TOWER_PM_SEGMENTATION.siteType.colors,
                  borderWidth: 0,
                }],
              }}
              options={{
                ...chartBaseOptions,
                cutout: '50%',
                plugins: {
                  ...chartBaseOptions.plugins,
                  legend: { display: true, position: 'bottom', labels: { ...chartBaseOptions.plugins.legend.labels, font: { family: 'Inter', size: 10, weight: '500' } } },
                  tooltip: {
                    ...chartBaseOptions.plugins.tooltip,
                    callbacks: { label: (ctx) => ` ${ctx.label}: ${ctx.raw.toLocaleString('en-IN')}` },
                  },
                },
              }}
            />
          </div>
        </div>

        {/* DG vs Non-DG */}
        <div className="card-surface p-6">
          <h3 className="mb-4 text-[13px] font-bold uppercase tracking-wide text-slate-500">DG vs Non-DG Sites</h3>
          <div className="mx-auto" style={{ height: 260, maxWidth: 280 }}>
            <Doughnut
              data={{
                labels: TOWER_PM_SEGMENTATION.dgNonDg.labels,
                datasets: [{
                  data: TOWER_PM_SEGMENTATION.dgNonDg.data,
                  backgroundColor: TOWER_PM_SEGMENTATION.dgNonDg.colors,
                  borderWidth: 0,
                }],
              }}
              options={{
                ...chartBaseOptions,
                cutout: '50%',
                plugins: {
                  ...chartBaseOptions.plugins,
                  legend: { display: true, position: 'bottom', labels: { ...chartBaseOptions.plugins.legend.labels, font: { family: 'Inter', size: 10, weight: '500' } } },
                  tooltip: {
                    ...chartBaseOptions.plugins.tooltip,
                    callbacks: { label: (ctx) => ` ${ctx.label}: ${ctx.raw.toLocaleString('en-IN')}` },
                  },
                },
              }}
            />
          </div>
        </div>

        {/* 5G vs 4G */}
        <div className="card-surface p-6">
          <h3 className="mb-4 text-[13px] font-bold uppercase tracking-wide text-slate-500">5G GNB vs 4G Only</h3>
          <div className="mx-auto" style={{ height: 260, maxWidth: 280 }}>
            <Doughnut
              data={{
                labels: TOWER_PM_SEGMENTATION.fiveG.labels,
                datasets: [{
                  data: TOWER_PM_SEGMENTATION.fiveG.data,
                  backgroundColor: TOWER_PM_SEGMENTATION.fiveG.colors,
                  borderWidth: 0,
                }],
              }}
              options={{
                ...chartBaseOptions,
                cutout: '50%',
                plugins: {
                  ...chartBaseOptions.plugins,
                  legend: { display: true, position: 'bottom', labels: { ...chartBaseOptions.plugins.legend.labels, font: { family: 'Inter', size: 10, weight: '500' } } },
                  tooltip: {
                    ...chartBaseOptions.plugins.tooltip,
                    callbacks: { label: (ctx) => ` ${ctx.label}: ${ctx.raw.toLocaleString('en-IN')}` },
                  },
                },
              }}
            />
          </div>
        </div>
      </div>

      {/* ── 9. 3-Consecutive PM WO Pending ── */}
      <SectionTitle dot={palette.warning}>3-Consecutive PM WO Pending</SectionTitle>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3 mb-4">
        <KpiCard
          label="Total Sites at 3× Risk"
          value={`${filteredConsec.length}`}
          sub={`${TOWER_PM_CONSECUTIVE_WO.sites.length} zone-wide · Filter active: ${activeCircle === 'ALL' ? 'All' : activeCircle}`}
          dot={palette.danger}
          valueColor={palette.danger}
        />
        <KpiCard
          label="Worst CMP"
          value="Srinagar"
          sub="42 sites · Geographic access challenges"
          dot={palette.warning}
          valueColor={palette.warning}
        />
        <KpiCard
          label="Penalty Multiplier"
          value="3×"
          sub="Activates if Mar'26 WO also missed"
          dot={palette.danger}
          valueColor={palette.danger}
        />
      </div>
      <div className="card-surface p-5">
        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-end">
          <SearchField value={consecSearch} onChange={(v) => { setConsecSearch(v); setConsecPage(PAGE_SIZE); }} placeholder="Search site ID..." className="flex-1 sm:min-w-[220px]" />
          <FilterSelect label="CMP" value={consecCmpFilter} onChange={(v) => { setConsecCmpFilter(v); setConsecPage(PAGE_SIZE); }} options={consecCmpOptions} />
        </div>
        {filteredConsec.length ? (
          <>
            <div className="table-scroll-shell">
              <table className="evidence-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Site ID</th>
                    <th>Facility ID</th>
                    <th>CMP</th>
                    <th>State</th>
                    <th>Site Type</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredConsec.slice(0, consecPage).map((row, i) => (
                    <tr key={`consec-${row.site_id}-${i}`}>
                      <td className="mono-cell text-slate-400">{i + 1}</td>
                      <td className="mono-cell font-medium text-slate-700">{row.site_id}</td>
                      <td className="mono-cell text-slate-500">{row.fac_id}</td>
                      <td>{row.cmp}</td>
                      <td>
                        <span className="inline-flex rounded-full px-2 py-1 text-[10px] font-semibold text-white" style={{ backgroundColor: CIRCLE_COLORS[row.state] ?? palette.slate }}>
                          {row.state}
                        </span>
                      </td>
                      <td className="mono-cell">{row.site_type}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {consecPage < filteredConsec.length && (
              <LoadMoreButton remaining={filteredConsec.length - consecPage} onClick={() => setConsecPage((p) => p + PAGE_SIZE)} />
            )}
          </>
        ) : (
          <EmptyState icon="search" />
        )}
      </div>

      {/* ── 10. Reference: De-scope & RFE1 ── */}
      <SectionTitle dot={palette.slateDeep}>Reference: De-scope &amp; RFE1</SectionTitle>
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {/* De-scope */}
        <div className="card-surface p-5">
          <h3 className="mb-3 text-[13px] font-bold uppercase tracking-wide text-slate-500">De-scoped Sites ({TOWER_PM_DESCOPE.length})</h3>
          <div className="table-scroll-shell" style={{ maxHeight: 360 }}>
            <table className="evidence-table">
              <thead className="sticky top-0 z-10">
                <tr>
                  <th>#</th>
                  <th>Site ID</th>
                  <th>CMP / Fac ID</th>
                  <th>State</th>
                </tr>
              </thead>
              <tbody>
                {TOWER_PM_DESCOPE.slice(0, descopePage).map((row, i) => (
                  <tr key={`ds-${row.site_id}-${i}`}>
                    <td className="mono-cell text-slate-400">{i + 1}</td>
                    <td className="mono-cell font-medium text-slate-700">{row.site_id}</td>
                    <td className="text-slate-600">{row.cmp}</td>
                    <td className="mono-cell">{row.state}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {descopePage < TOWER_PM_DESCOPE.length && (
            <div className="mt-3">
              <LoadMoreButton remaining={TOWER_PM_DESCOPE.length - descopePage} onClick={() => setDescopePage((p) => p + PAGE_SIZE)} />
            </div>
          )}
        </div>

        {/* RFE1 */}
        <div className="card-surface p-5">
          <h3 className="mb-3 text-[13px] font-bold uppercase tracking-wide text-slate-500">RFE1 Sites ({TOWER_PM_RFE1.length})</h3>
          <div className="table-scroll-shell" style={{ maxHeight: 360 }}>
            <table className="evidence-table">
              <thead className="sticky top-0 z-10">
                <tr>
                  <th>#</th>
                  <th>Site ID</th>
                  <th>CMP</th>
                  <th>State</th>
                </tr>
              </thead>
              <tbody>
                {TOWER_PM_RFE1.slice(0, rfe1Page).map((row, i) => (
                  <tr key={`rfe-${row.site_id}-${i}`}>
                    <td className="mono-cell text-slate-400">{i + 1}</td>
                    <td className="mono-cell font-medium text-slate-700">{row.site_id}</td>
                    <td className="text-slate-600">{row.cmp}</td>
                    <td className="mono-cell">{row.state}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {rfe1Page < TOWER_PM_RFE1.length && (
            <div className="mt-3">
              <LoadMoreButton remaining={TOWER_PM_RFE1.length - rfe1Page} onClick={() => setRfe1Page((p) => p + PAGE_SIZE)} />
            </div>
          )}
        </div>
      </div>

      {/* ── 11. Executive Insights ── */}
      <InsightBanner chips={TOWER_PM_INSIGHT_CHIPS} />

      <div className="grid grid-cols-1 gap-4">
        {TOWER_PM_INSIGHTS.map((insight) => (
          <InsightCard
            key={insight.title}
            tone={insight.tone}
            title={insight.title}
            exposure={insight.exposure}
            body={insight.body}
            actionTitle={insight.actionTitle}
            action={insight.action}
            chips={insight.chips}
          >
            {insight.metrics?.length ? (
              <div className="mt-3 flex flex-wrap gap-3">
                {insight.metrics.map((m) => {
                  const mAccent = toneAccent(m.tone || m.color || 'blue');
                  return (
                    <div key={m.label} className="rounded-lg px-3 py-2 text-center" style={{ backgroundColor: mAccent.soft }}>
                      <div className="text-[14px] font-bold" style={{ color: mAccent.line }}>{m.value}</div>
                      <div className="text-[10px] font-medium text-slate-500">{m.label}</div>
                    </div>
                  );
                })}
              </div>
            ) : null}
          </InsightCard>
        ))}
      </div>

      {/* ── 12. Prioritised Remediation Plan ── */}
      <div className="card-surface border border-emerald-200 bg-emerald-50/80 p-6">
        <div className="text-[10px] font-semibold uppercase tracking-[0.06em] text-emerald-700">Recoverable Upside</div>
        <div className="mt-2 text-[13px] font-semibold leading-6 text-emerald-900">{TOWER_PM_RECOVERABLE_BANNER.text}</div>
      </div>

      <div className="card-surface p-6">
        <SectionTitle dot={palette.success}>Prioritised Remediation Plan</SectionTitle>
        <div className="table-scroll-shell">
          <table className="action-table">
            <thead>
              <tr>
                <th>Priority</th>
                <th>Action</th>
                <th>Stage Impact</th>
                <th>CMPs Impacted</th>
                <th>Owner</th>
                <th>Expected Impact</th>
              </tr>
            </thead>
            <tbody>
              {TOWER_PM_ACTIONS.map((row) => (
                <tr key={row.action.slice(0, 40)}>
                  <td><PriorityBadge priority={row.priority} /></td>
                  <td>
                    <div className="max-w-[400px] whitespace-normal font-semibold text-slate-700">{row.action}</div>
                  </td>
                  <td>
                    <span className="status-badge status-badge-blue">{row.stageImpact}</span>
                  </td>
                  <td className="max-w-[200px] whitespace-normal text-[11px] leading-5 text-slate-600">{row.cmps}</td>
                  <td className="text-slate-600">{row.owner}</td>
                  <td className="max-w-[260px] whitespace-normal text-[11px] leading-5 text-slate-600">{row.expectedImpact}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
